import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useRef } from 'react';
import { normalizeMultiText } from '../util';

interface MultilineTextBoxProps {
    value:string|null,
    placeholder?:string|undefined,
    onChange?: ((e:React.ChangeEvent<HTMLTextAreaElement>) => void)|undefined,
    disabled?: boolean,
    textId?: string|undefined,
    onTabNext?: (() => boolean)|undefined,
    onTabPrevious?: (() => boolean)|undefined,
    fontSize?: string|undefined,
    noborder?: boolean,
    maxLength?: number|undefined,
    alignRight?: boolean,
    normalize?: boolean,
    [x:string]: any
};

export function ReplaceCrToCrLf(str: string) {
  if (!str) return '';
  const temp = str.replaceAll(/(\r\n)/gm, '\n');    // 안전하게..
  return temp.replaceAll(/(\n)/gm, '\r\n');
}

function MultilineTextBox({
  value,
  placeholder = undefined,
  onChange = () => {},
  disabled = false,
  textId = undefined,
  onTabNext = undefined,
  onTabPrevious = undefined,
  fontSize = undefined,
  noborder = false,
  maxLength = undefined,
  alignRight = false,
  focusableWhenDisabled = false,
  normalize = true,
  ...other
}: MultilineTextBoxProps) {
  const inputRef = useRef<HTMLTextAreaElement>(null);

  function changeWrapper(e:React.ChangeEvent<HTMLTextAreaElement>) {
    if (maxLength !== undefined && maxLength !== null && maxLength > 0) {
      // Don't count newlines when counting max length
      if (e.target.value && e.target.value.replace(/(\r\n|\n|\r)/gm, " ").length > maxLength) {
        return;
      }
    }

    if (onChange) {
      onChange(e);
    }
  }
  
  function processTabNext(e:React.KeyboardEvent<HTMLTextAreaElement>) {
    if (onTabNext) {
      const tabProcessed = onTabNext();
      if (tabProcessed) {
        e.preventDefault();
        return;
      }
    }
  }

  function processTabPrevious(e:React.KeyboardEvent<HTMLTextAreaElement>) {
    if (onTabPrevious) {
      const tabProcessed = onTabPrevious();
      if (tabProcessed) {
        e.preventDefault();
        return;
      }
    }
  }

  async function handleBlur(e: React.FocusEvent<HTMLTextAreaElement>) {
    const target = e.target as HTMLTextAreaElement;

    if (normalize && onChange) {
      const normalized = normalizeMultiText(target.value);
      if (normalized !== target.value) {
        const syntheticEvent = {
          ...e,
          target: { ...target, value: normalized }
        } as unknown as React.ChangeEvent<HTMLTextAreaElement>;

        onChange(syntheticEvent);
      }
    }
  }

  function onKeyDown(e:React.KeyboardEvent<HTMLTextAreaElement>) {
    const tab = 9;
    const enter = 13;

    if (disabled && focusableWhenDisabled) {
      if (!(e.ctrlKey && (e.keyCode === 67 || e.keyCode === 65))) {
        e.stopPropagation();
        e.preventDefault();
        return;
      }
    }

    if (e.keyCode === tab) {
      if (e.shiftKey) {
        processTabPrevious(e);
      } else {
        processTabNext(e);
      }
    } else if (e.keyCode === enter) {
      // There are onEnter handlers in various parts of application that can intervene with the typing.
      // To avoid it, Enter keys are handled manually
      e.preventDefault();
      e.stopPropagation();

      if (inputRef.current) {
        const currentValue = value || '';
        if (maxLength && currentValue.replace(/(\r\n|\n|\r)/gm, " ").length >= maxLength) {
          return; // Don't allow
        }
        const nextValue = currentValue.substring(0, inputRef.current.selectionStart) + '\n' + currentValue.substring(inputRef.current.selectionEnd, currentValue.length);

        onChange({target: {
          value: nextValue
        }} as React.ChangeEvent<HTMLTextAreaElement>);

        const caretPos = inputRef.current.selectionStart + 1;

        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.selectionEnd = caretPos;

            // Hack to make sure scroll to caret position (when hitting enter)
            inputRef.current.blur();
            inputRef.current.focus();
          }
        }, 1);
      }
    }
  }

  const inputId = textId || _.uniqueId('ifms-multiline-text-');

  let cssClass = 'e-ifms-multiline-input e-ifms-input e-outline ifms-tabable';
  if(disabled && focusableWhenDisabled){
    cssClass = `${cssClass} e-ifms-focusable-when-disabled`;
  }
  if (noborder) {
    cssClass = `${cssClass} ifms-noborder-input`;
  }
  if (!disabled) {
    cssClass = `${cssClass} ifms-tabable-active`;
  }
  if (alignRight) {
    cssClass = `${cssClass} ifms-table-align-right`;
  }

  const style = !!other.style ? other.style : {};
  const customStyle = !!fontSize ? { ...style, fontSize } : style;
  return (
    <textarea
      id={inputId}
      ref={inputRef}
      className={cssClass}
      style={customStyle}
      value={value ?? ''}
      onChange={changeWrapper}
      disabled={disabled && !focusableWhenDisabled}
      placeholder={placeholder}
      data-tooltip="-from-value"
      data-tooltip-onlywhencutoff
      onBlur={handleBlur}
      onKeyDown={onKeyDown} 
      {...other} 
    />
  );
}

MultilineTextBox.defaultProps = {
  onChange: () => {},
  value: '',
  disabled: false,
  alignRight: false,
  placeholder: '',
  noborder: false,
  focusableWhenDisabled: false
};

MultilineTextBox.propTypes = {
  onChange: PropTypes.func.isRequired,
  value: PropTypes.any,
  placeholder: PropTypes.string.isRequired,
  disabled: PropTypes.bool.isRequired,
  textId: PropTypes.string,
  onTabNext: PropTypes.func,
  onTabPrevious: PropTypes.func,
  fontSize: PropTypes.string,
  noborder: PropTypes.bool.isRequired,
  maxLength: PropTypes.number,
  alignRight: PropTypes.bool.isRequired,
  focusableWhenDisabled: PropTypes.bool,
};

export default React.memo(MultilineTextBox);
